import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store/root-reducer';

import { previewFullName, validateDraft } from '../domain/profile-identity-draft.validation';

const selectSlice = (state: RootState) => state.editProfileIdentity;

export const selectProfileId = (state: RootState) => selectSlice(state).profileId;
export const selectScope = (state: RootState) => selectSlice(state).scope;
export const selectReferenceNames = (state: RootState) => selectSlice(state).reference;
export const selectDraft = (state: RootState) => selectSlice(state).draft;
export const selectCollectedNames = (state: RootState) => selectSlice(state).collected;
export const selectValidationErrors = (state: RootState) => selectSlice(state).validationErrors;
export const selectError = (state: RootState) => selectSlice(state).error;
export const selectSavedIdentity = (state: RootState) => selectSlice(state).savedIdentity;
export const selectIsSaving = (state: RootState) => selectSlice(state).saveStatus === 'saving';

/** Derived: is the current draft valid? Memoised on the draft reference. */
export const selectIsDraftValid = createSelector(
  selectDraft,
  selectScope,
  (draft, scope) => Object.keys(validateDraft(draft, scope)).length === 0,
);

/**
 * Derived: what to show under each field; the live rule first, the server's
 * verdict second.
 *
 * Without the live half the dialog dead-ends: Save is disabled while the draft
 * is invalid, so the saga's validation branch never runs, so nothing ever
 * explains *why* it is disabled. Showing it live needs no "touched" tracking
 * either; the dialog is seeded from an existing profile, so a rule can only
 * break after the admin types (and if a stored value is genuinely invalid,
 * saying so on open is the useful behaviour, not a false accusation).
 *
 * The live message wins over the stored one: a 409 ("this URL is taken") is
 * about a value the admin has since edited, so a newly malformed URL is the
 * more actionable thing to say.
 */
export const selectFieldErrors = createSelector(
  selectDraft,
  selectScope,
  selectValidationErrors,
  (draft, scope, stored): Record<string, string | undefined> => {
    const live = validateDraft(draft, scope);
    return {
      firstName: live.firstName ?? stored.firstName,
      lastName: live.lastName ?? stored.lastName,
      linkedinUrl: live.linkedinUrl ?? stored.linkedinUrl,
    };
  },
);

/** Derived: the full name the save will produce; the dialog's read-only preview. */
export const selectPreviewFullName = createSelector(
  selectDraft,
  selectScope,
  selectReferenceNames,
  previewFullName,
);

/**
 * Derived: per name field, the value to offer as a reset; only when the field
 * differs from it. For an admin, the collected value, and only on a profile that
 * was actually corrected. For a licence, the reference name (#703), which a
 * cleared field shows anyway, so a blank field offers nothing either. Anything
 * else would show a hint identical to what is already in the input.
 */
export const selectResettableNames = createSelector(
  selectDraft,
  selectCollectedNames,
  selectScope,
  selectReferenceNames,
  (draft, collected, scope, reference) =>
    scope === 'licence'
      ? {
          firstName:
            draft.firstName.trim() && draft.firstName.trim() !== reference.firstName
              ? reference.firstName
              : null,
          lastName:
            draft.lastName.trim() && draft.lastName.trim() !== reference.lastName
              ? reference.lastName
              : null,
        }
      : {
          firstName:
            collected.editedAt && draft.firstName !== collected.firstName
              ? collected.firstName
              : null,
          lastName:
            collected.editedAt && draft.lastName !== collected.lastName ? collected.lastName : null,
        },
);
